import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, signInWithCredential, signOut, connectAuthEmulator } from "firebase/auth";
import { getFirestore, doc, setDoc, serverTimestamp, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { Capacitor } from "@capacitor/core";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const requiredConfigKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
const missingKeys = requiredConfigKeys.filter(key => !firebaseConfig[key]);

if (missingKeys.length > 0) {
  console.error('[Firebase] Missing configuration keys:', missingKeys);
}

let app;
let auth;
let db;
let storage;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
} catch (error) {
  console.error('[Firebase] Failed to initialize Firebase:', error);
  throw error;
}

// Local verification only. A build made with REACT_APP_USE_EMULATORS=true talks
// to the Firebase emulators, so admin-only screens can be driven end to end
// without production credentials. The variable is never in .env: CRA inlines
// it at build time, so a production bundle does not even contain this branch.
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
}

// The CRA/Workbox service worker already owns scope "/". Registering the
// messaging worker there too would replace it on every load — each swap tears
// down the push subscription, invalidating the FCM token, so pushes silently
// stop arriving. Give messaging its own scope (the same one the SDK uses by
// default) so both workers coexist.
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

// Each device keeps its own token doc under users/{uid}/fcmTokens so a second
// device never overwrites the first (the old single users/{uid}.fcmToken field
// meant only the most recently opened device received pushes). The doc id is a
// stable hash of the token, so re-registering an unchanged token updates the
// same doc instead of piling up duplicates.
export async function tokenDocId(token) {
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Non-secure contexts have no crypto.subtle; a deterministic 32-bit hash is
    // enough for a per-device id (a collision only merges two tokens for one
    // user, which self-heals when the stale one is pruned on send failure).
    let h = 5381;
    for (let i = 0; i < token.length; i++) h = ((h << 5) + h + token.charCodeAt(i)) >>> 0;
    return 'h' + h.toString(16);
  }
}

// Writes one device's token. `platform` tells the sender how to shape the
// payload: a data-only message is displayed by our service worker on web, but
// on native Android it would arrive silently — those tokens need an FCM
// `notification` block instead (see functions/pushTokens.js). Tokens written
// before this field existed are treated as 'web', which is what they were.
export async function saveFcmToken(uid, token, platform = 'web', extra = {}) {
  const id = await tokenDocId(token);
  await setDoc(
    doc(db, 'users', uid, 'fcmTokens', id),
    {
      ...extra,
      token,
      platform,
      userAgent: (navigator.userAgent || '').slice(0, 300),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

// Registers the messaging SW, mints a token, stores it, and wires the
// foreground handler. Shared by the gesture (enableNotifications) and load
// (refreshFcmToken) paths — both need the identical token + listener setup once
// permission is granted; only who asks for permission differs.
async function registerTokenAndListener(uid) {
  const registration = await navigator.serviceWorker.register(
    '/firebase-messaging-sw.js',
    { scope: FCM_SW_SCOPE }
  );
  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (token) {
    await saveFcmToken(uid, token, 'web');
  }

  // Data-only pushes that arrive while a tab is open never reach the SW's
  // onBackgroundMessage — display them here or they are lost entirely.
  onMessage(messaging, (payload) => {
    const data = payload?.data;
    if (!data?.title) return;
    registration.showNotification(data.title, {
      body: data.body || '',
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: data.type || 'speaklab',
      data: { url: data.url || '/' },
    });
  });

  return Boolean(token);
}

// Opt-in from a user gesture (a tap). Notification.requestPermission() is
// unreliable without a gesture and is outright required to be gesture-triggered
// on iOS/installed PWAs, so this must never be called from an effect or the
// auth callback. Returns the resulting permission-ish status for the UI to act
// on: 'granted' | 'denied' | 'default' | 'unsupported'.
export async function enableNotifications(uid) {
  if (!uid || !process.env.REACT_APP_FIREBASE_VAPID_KEY) return 'unsupported';

  try {
    if (!(await isSupported())) return 'unsupported';
    if (Notification.permission === 'denied') return 'denied';

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission; // 'denied' | 'default'

    await registerTokenAndListener(uid);
    return 'granted';
  } catch (error) {
    console.error('[Firebase] enableNotifications failed:', error);
    return 'unsupported';
  }
}

// Load-time refresh for users who already granted permission. Never prompts —
// it bails unless permission is already 'granted' — so it is safe to call from
// the auth callback and keeps an opted-in user's token fresh across sessions.
export async function refreshFcmToken(uid) {
  if (!uid || !process.env.REACT_APP_FIREBASE_VAPID_KEY) return;

  try {
    if (!(await isSupported())) return;
    if (Notification.permission !== 'granted') return;

    await registerTokenAndListener(uid);
  } catch (error) {
    console.warn('[Firebase] refreshFcmToken skipped:', error.message);
  }
}

// The modular Web SDK has no onTokenRefresh callback (it was removed), so we
// actively re-mint the token instead of only doing it once at app load: on
// every foreground return and on a periodic timer while the app stays open.
// registerTokenAndListener is idempotent — getToken returns the *current*
// token and the SW registration is reused — so a token that rotated while the
// app was closed (or during a long-lived session) is re-written without the
// user doing anything. This is the practical web equivalent of onTokenRefresh
// and closes the "stale token until the next full reload" gap. Returns a
// cleanup function; call it when the user signs out / the effect tears down.
const TOKEN_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h

export function watchFcmToken(uid) {
  if (!uid) return () => {};

  let stopped = false;
  const tick = () => { if (!stopped) refreshFcmToken(uid).catch(() => {}); };

  tick(); // immediate refresh — covers rotation since the previous session

  const onVisible = () => {
    if (document.visibilityState === 'visible') tick();
  };
  document.addEventListener('visibilitychange', onVisible);
  const interval = setInterval(tick, TOKEN_REFRESH_INTERVAL_MS);

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(interval);
  };
}

// Popups are unreliable in an installed PWA (standalone display) and inside
// in-app webviews. Fall back to a full-page redirect: onAuthStateChanged in
// App.js creates/refreshes the user doc afterwards, so the redirect path needs
// no extra bookkeeping.
const POPUP_UNAVAILABLE = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
]);

// Inside the Android app Google refuses the web popup/redirect
// (disallowed_useragent), so there the system account picker (Credential
// Manager, @capacitor-firebase/authentication) returns an ID token and the JS
// SDK signs in with it. `skipNativeAuth` (capacitor.config.ts) keeps the one
// session in the JS SDK, as on the web. The result has the same shape as the
// popup's; null means "nothing happened" (picker cancelled, or the redirect
// fallback is navigating away).
async function signInWithGoogleNative() {
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  let result;
  try {
    result = await FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: true });
  } catch (error) {
    if (/cancel/i.test(String(error?.message || error?.code || ''))) return null;
    throw error;
  }
  const idToken = result?.credential?.idToken;
  if (!idToken) throw new Error('Google did not return a sign-in token.');
  return signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
}

// Signing out also clears the native Google session, so the next sign-in
// shows the account picker instead of reusing the last account.
export async function signOutEverywhere() {
  if (Capacitor.isNativePlatform()) {
    try {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      await FirebaseAuthentication.signOut();
    } catch { /* no native session — nothing to clear */ }
  }
  return signOut(auth);
}

export async function signInWithGoogle() {
  if (Capacitor.isNativePlatform()) return signInWithGoogleNative();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  try {
    return await signInWithPopup(auth, provider);
  } catch (error) {
    if (POPUP_UNAVAILABLE.has(error.code)) {
      await signInWithRedirect(auth, provider);
      return null; // the page navigates away; nothing else to do here
    }
    throw error; // popup-closed-by-user etc. — a real cancellation
  }
}

export { auth, db, storage, GoogleAuthProvider, signInWithPopup };
export default app;
